/** @jsx isolateComponent */
import { css } from '@emotion/core';

import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import { createUniqueCustomProperties } from '../../../utils/createUniqueCustomProperty';
import { Colors, Typography } from '../../../theme';
import { Props as AvatarProps } from './Avatar';

export type Props = {
  /**
   * `aria-label` attribute for better screenreader text.
   */
  ariaLabel?: string;
  /**
   * Generally a rendered `Avatar`, `AvatarWithTooltip`, or `AvatarGroup` component.
   * Behaves like a Web Component `slot`.
   */
  avatars: React.ReactNode;
  /**
   * Text children to render beside the `Avatar`s.
   * Should generally be `AvatarText` components.
   */
  children: React.ReactNode;
  /**
   * Whether to text across multiple lines.
   */
  multiline?: boolean;
  /**
   * The `size` of the `Avatar`s that render within the group.
   */
  size: AvatarProps['size'];
};

// These custom properties are used to style `AvatarText` via `AvatarsWithText`.
// This prevents consumers from needing to / being able to pass a `size` prop
// to `AvatarText` and controlling it independently of `AvatarsWithText`.
const Vars = createUniqueCustomProperties('AvatarsWithText', [
  'textDisplay',
  'textFontSizeLight',
  'textLineHeightLight',
  'textFontSizeHeavy',
  'textLineHeightHeavy',
]);
const Styles = labelStyles('AvatarsWithText', {
  root: css`
    ${Vars.textDisplay}: inline-block;
    ${Vars.textFontSizeLight}: 12px;
    ${Vars.textLineHeightLight}: 15px;
    ${Vars.textFontSizeHeavy}: 12px;
    ${Vars.textLineHeightHeavy}: 15px;

    display: grid;
    grid-template-columns: auto 1fr;
    gap: 5px;
    align-items: center;

    &[data-multiline] {
      ${Vars.textDisplay}: block;
    }

    &[data-size='18'] {
      ${Vars.textFontSizeLight}: ${Typography.smallPrint3.fontSize};
      ${Vars.textLineHeightLight}: ${Typography.smallPrint3.lineHeight};
      ${Vars.textFontSizeHeavy}: ${Typography.smallPrint3Bold.fontSize};
      ${Vars.textLineHeightHeavy}: ${Typography.smallPrint3Bold.lineHeight};
    }

    &[data-size='25']:not([data-multiline]),
    &[data-size='40']:not([data-multiline]) {
      ${Vars.textFontSizeLight}: ${Typography.smallPrint1.fontSize};
      ${Vars.textLineHeightLight}: ${Typography.smallPrint1.lineHeight};
      ${Vars.textFontSizeHeavy}: ${Typography.smallPrint1Bold.fontSize};
      ${Vars.textLineHeightHeavy}: ${Typography.smallPrint1Bold.lineHeight};
    }

    &[data-size='25'][data-multiline] {
      ${Vars.textFontSizeLight}: ${Typography.smallPrint3.fontSize};
      ${Vars.textLineHeightLight}: ${Typography.smallPrint3.lineHeight};
      ${Vars.textFontSizeHeavy}: ${Typography.smallPrint3Bold.fontSize};
      ${Vars.textLineHeightHeavy}: ${Typography.smallPrint3Bold.lineHeight};
    }

    &[data-size='40'][data-multiline] {
      ${Vars.textFontSizeLight}: ${Typography.smallPrint3.fontSize};
      ${Vars.textLineHeightLight}: ${Typography.smallPrint3.lineHeight};
      ${Vars.textFontSizeHeavy}: ${Typography.smallPrint1Bold.fontSize};
      ${Vars.textLineHeightHeavy}: ${Typography.smallPrint1Bold.lineHeight};
    }
  `,
  avatars: css`
    display: block;
  `,
  content: css`
    display: block;
    position: relative;
    top: -1px;
    line-height: max(
      var(${Vars.textLineHeightLight}),
      var(${Vars.textLineHeightHeavy})
    );
  `,
  text: css`
    display: var(${Vars.textDisplay});
    &[data-kind='light'] {
      color: ${Colors.gray60};
      font-weight: 400;
      font-size: calc(1px * var(${Vars.textFontSizeLight}));
      line-height: var(${Vars.textLineHeightLight});
    }
    &[data-kind='heavy'] {
      color: ${Colors.gray};
      font-weight: 700;
      font-size: calc(1px * var(${Vars.textFontSizeHeavy}));
      line-height: var(${Vars.textLineHeightHeavy});
    }
  `,
});

/**
 * Text node to render as `children` within `AvatarsWithText`.
 * 🚨 Only meant to be used within `AvatarsWithText`.
 */
export const AvatarText = (props: {
  ariaLabel?: string;
  children: React.ReactNode;
  kind: 'heavy' | 'light';
}) => {
  return (
    <span
      aria-label={props.ariaLabel}
      css={Styles.text}
      data-kind={props.kind}
      // `role="text"` isn't technically valid (yet), but it allows
      // VoiceOver to read the content more clearly and prevents it
      // from splitting text. Axe also complains about this, but will
      // stop when the extension uses `axe-core@4.2.0`. See issue:
      // https://github.com/dequelabs/axe-core/issues/1597
      // eslint-disable-next-line jsx-a11y/aria-role
      role="text"
    >
      {props.children}
    </span>
  );
};

/**
 * Displays a group of `Avatar`s with contextual text beside them.
 */
export const AvatarsWithText = (props: Props) => {
  return (
    <div
      aria-label={props.ariaLabel}
      css={Styles.root}
      data-multiline={props.multiline ? '' : undefined}
      data-size={props.size}
    >
      <div css={Styles.avatars}>{props.avatars}</div>
      <div css={Styles.content}>{props.children}</div>
    </div>
  );
};
